import React, { useState, useContext, useEffect } from "react";
import AuthContext from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import authService from "../services/authService";

const PasswordRecovery = () => {
  const { user, isInitialized } = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isInitialized && user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, isInitialized]);

  const validateEmail = (email) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Invalid email address");
      return;
    }

    setLoading(true);

    try {
      await authService.recoverPassword(email);
      setSuccess(true);
    } catch (err) {
      const errorMessage = err.response?.data?.detail ||
                          err.response?.data?.message ||
                          "Failed to send recovery email. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Show loading while checking auth state
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
      {/* Logo or App Name */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center mb-2">
          <h1 className="text-3xl font-bold org-gradient-text">
            AddFlint
          </h1>
        </div>
      </div>

      {/* Password Recovery Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-8 rounded-2xl shadow-md w-full max-w-md border border-gray-200"
      >
        {/* Back to Login Link */}
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-gray-600 hover:text-purple-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Login
        </Link>

        <h2 className="text-2xl font-bold mb-2 text-gray-800">Password Recovery</h2>
        <p className="text-sm text-gray-600 mb-6">
          A password recovery email will be sent to the registered account.
        </p>

        {/* Success Message */}
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl"
          >
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-3 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800 mb-1">
                  Email sent successfully!
                </p>
                <p className="text-xs text-green-700">
                  Please check your inbox for password reset instructions. If you don't see the email, check your spam folder.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/login"
                className="text-sm font-medium text-green-700 hover:text-green-800 underline"
              >
                Return to Login
              </Link>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-6 p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center text-red-700"
          >
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </motion.div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  placeholder="your.email@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  className={`w-full pl-10 pr-3 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                    error
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                  }`}
                />
              </div>
            </div>

            {/* Continue Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "org-gradient shadow-md hover:shadow-lg"
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Email...
                </>
              ) : (
                "Continue"
              )}
            </button>
          </form>
        )}

        {/* Additional Help */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Need additional help?{" "}
            <button
              type="button"
              onClick={() => window.open('mailto:support@addflint.io', '_blank')}
              className="font-medium text-purple-600 hover:text-purple-500"
            >
              Contact AddFlint Support
            </button>
          </p>
        </div>
      </motion.div>

      {/* Footer */}
      <p className="mt-8 text-center text-xs text-gray-500">
        &copy; 2025 AddFlint. All rights reserved.
      </p>
    </div>
  );
};

export default PasswordRecovery;
